import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StorageConstants } from '@warema/shared/models';
import { BehaviorSubject, from, fromEvent, merge } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { UserCentricsService } from './user-centrics.service';
import { ConsentDetails, ConsentState, ServiceData, UCCmp } from '../models/UserCentrics';
import { localStorageKey, UserCentricsConstants } from '../models/UserCentricsConstants';

interface UCWindow extends Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __ucCmp?: UCCmp;
}

declare let window: UCWindow;

@Injectable()
export class UserCentricsBrowserService extends UserCentricsService {
    private readonly _translateService = inject(TranslateService);

    private readonly _givenConsents = new BehaviorSubject<Record<string, ServiceData>>({});

    constructor() {
        super();

        fromEvent<CustomEvent<ConsentDetails>>(window, UserCentricsConstants.UC_CONSENT)
            .pipe(map(x => x.detail.services))
            .subscribe(this._givenConsents);

        setTimeout(() =>
            merge(
                // either UC initializes and fires an event
                fromEvent<CustomEvent>(window, UserCentricsConstants.INITIALIZED).pipe(map(() => true)),
                // or UC is already initialized
                from(window.__ucCmp?.isInitialized() ?? Promise.resolve(false)).pipe(filter(x => x)),
            )
                .pipe(first())
                .subscribe(() => void this._initialize()),
        );
    }

    public set redirectUrl(url: string) {
        localStorage.setItem(StorageConstants.CONSENT_REDIRECT_URL, url);
    }

    public get redirectUrl(): string | null {
        const url = localStorage.getItem(StorageConstants.CONSENT_REDIRECT_URL);
        localStorage.removeItem(StorageConstants.CONSENT_REDIRECT_URL);
        return url;
    }

    public async hasUserCurrentlyConsented(serviceName: string): Promise<boolean> {
        const key = await this._getServiceKeyByServiceName(serviceName);
        if (!key) {
            return false;
        }

        const internalConsent = this._givenConsents.getValue()[key]?.consent?.given ?? false;
        this._syncServiceConsentToLocalStorage(key, internalConsent);

        return internalConsent;
    }

    public hasUserConsented(serviceName: string): BehaviorSubject<boolean> {
        const behaviorSubject = new BehaviorSubject<boolean>(false);
        this.isLoaded
            .pipe(
                first(x => x),
                switchMap(() => from(this._getServiceKeyByServiceName(serviceName))),
                switchMap(key => this._givenConsents.pipe(map(consents => ({ consents, key })))),
                map(({ consents, key }) => {
                    if (key) {
                        const consent = consents[key]?.consent?.given ?? false;
                        this._syncServiceConsentToLocalStorage(key, consent);
                        return consent;
                    }
                    return false;
                }),
            )
            .subscribe(behaviorSubject);
        return behaviorSubject;
    }

    public showSecondLayer(): void {
        window.__ucCmp?.showSecondLayer();
    }

    public async acceptConsent(serviceName: string): Promise<void> {
        const key = await this._getServiceKeyByServiceName(serviceName);
        if (!key) {
            console.error(`Service ${serviceName} not found.`);
            return;
        }

        const service = this._givenConsents.getValue()[key];
        if (!service) {
            console.error(`Service ${serviceName} with key ${key} not found.`);
            return;
        }

        try {
            await window.__ucCmp?.updateServicesConsents([{ id: key, consent: true }]);
            await window.__ucCmp?.saveConsents('EXPLICIT');
        } catch (acceptConsentError) {
            console.error(`Failed to accept consent for service ${serviceName}:`, acceptConsentError);
        }
    }

    private async _getServiceKeyByServiceName(serviceName: string): Promise<string | null> {
        const consentDetails = await window.__ucCmp?.getConsentDetails();
        if (!consentDetails) {
            return null;
        }
        return Object.keys(consentDetails.services).find(x => consentDetails.services[x].name === serviceName) ?? null;
    }

    private async _initialize(): Promise<void> {
        this.isLoaded.next(true);

        await this._updateLanguage(this._translateService.getCurrentLang() ?? 'de');

        const services = (await window.__ucCmp?.getConsentDetails())?.services;
        if (services) {
            this._givenConsents.next(services);
        }

        this._translateService.onLangChange
            .pipe(switchMap(langChangeEvent => this._updateLanguage(langChangeEvent.lang)))
            .subscribe();

        // if the app runs in an iframe with user centrics on both sides, the local storage goes out of sync
        this._syncStateToLocalStorage();
    }

    private async _updateLanguage(language: string): Promise<void> {
        const baseLanguage = language.split('-')[0];
        await window.__ucCmp?.changeLanguage(baseLanguage);
    }

    private _syncStateToLocalStorage(): void {
        const localStorageState = this._getLocalStorageState();
        if (localStorageState?.consent?.services == null) {
            return;
        }

        const internalState = this._givenConsents.getValue();
        let changes = false;

        for (const key of Object.keys(internalState)) {
            if (localStorageState.consent.services[key] == null) {
                continue;
            }

            localStorageState.consent.services[key].consent = internalState[key].consent?.given ?? false;
            changes = true;
        }

        if (changes) {
            this._setLocalStorageState(localStorageState);
        }
    }

    private _syncServiceConsentToLocalStorage(serviceKey: string, internalConsent: boolean): void {
        const localStorageState = this._getLocalStorageState();

        const serviceState = localStorageState?.consent?.services?.[serviceKey];
        if (serviceState == null || serviceState.consent === internalConsent) {
            return;
        }

        localStorageState.consent.services[serviceKey].consent = internalConsent;
        this._setLocalStorageState(localStorageState);
    }

    private _getLocalStorageState(): ConsentState | null {
        const localStorageItem = localStorage.getItem(localStorageKey);
        if (!localStorageItem) {
            return null;
        }

        return JSON.parse(localStorageItem) as ConsentState;
    }

    private _setLocalStorageState(state: ConsentState): void {
        localStorage.setItem(localStorageKey, JSON.stringify(state));
    }
}
